// Central helpers for the Visa module (frontend)
import { api } from "@/lib/api";

export const VISA_STATUSES = [
  { id: "draft",                 label: "Draft",                          phase: "Submission", color: "#5C6660", bg: "#F2F4F3" },
  { id: "submitted",             label: "Submitted",                      phase: "Submission", color: "#5C6660", bg: "#F2F4F3" },
  { id: "document_verification", label: "Document Verification",          phase: "Review",     color: "#2980B9", bg: "#EAF2F8" },
  { id: "documents_requested",   label: "Additional Docs Requested",      phase: "Review",     color: "#B26B00", bg: "#FFF4E5" },
  { id: "medical_approval",      label: "Medical Approval",               phase: "Review",     color: "#2980B9", bg: "#EAF2F8" },
  { id: "letter_draft",          label: "Invitation Letter Draft",        phase: "Letter",     color: "#7A5AC2", bg: "#EFE9FB" },
  { id: "admin_approval",        label: "Admin Approval",                 phase: "Letter",     color: "#7A5AC2", bg: "#EFE9FB" },
  { id: "letter_issued",         label: "Invitation Letter Issued",       phase: "Letter",     color: "#2A4D3E", bg: "#E6F0EB" },
  { id: "sent_to_patient",       label: "Sent to Patient",                phase: "Delivery",   color: "#2A4D3E", bg: "#E6F0EB" },
  { id: "visa_applied",          label: "Visa Applied",                   phase: "Application", color: "#D97757", bg: "#FBEDE5" },
  { id: "visa_approved",         label: "Visa Approved",                  phase: "Application", color: "#2A4D3E", bg: "#E6F0EB" },
  { id: "travel_confirmed",      label: "Travel Confirmed",               phase: "Application", color: "#2A4D3E", bg: "#E6F0EB" },
  { id: "closed",                label: "Closed",                         phase: "Application", color: "#5C6660", bg: "#F2F4F3" },
];

export const VISA_STATUS_BY_ID = Object.fromEntries(VISA_STATUSES.map((s) => [s.id, s]));
export const VISA_PHASES = ["Submission", "Review", "Letter", "Delivery", "Application"];

export const DOC_CATEGORIES = [
  { id: "passport",         label: "Passport" },
  { id: "medical_report",   label: "Medical Report" },
  { id: "photograph",       label: "Photograph" },
  { id: "previous_visa",    label: "Previous Visa" },
  { id: "companion_passport", label: "Companion Passport" },
  { id: "other",            label: "Other" },
];

// -------------- API helpers --------------
export const visaApi = {
  listStatuses: () => api.get("/visa/statuses").then((r) => r.data),

  listRequests: (params = {}) => api.get("/visa/requests", { params }).then((r) => r.data),
  getRequest: (id) => api.get(`/visa/requests/${id}`).then((r) => r.data),
  createRequest: (body) => api.post("/visa/requests", body).then((r) => r.data),
  updateRequest: (id, body) => api.patch(`/visa/requests/${id}`, body).then((r) => r.data),
  deleteRequest: (id) => api.delete(`/visa/requests/${id}`).then((r) => r.data),
  changeStatus: (id, status, note) =>
    api.post(`/visa/requests/${id}/status`, { status, note }).then((r) => r.data),

  // Single-shot upload with browser progress
  uploadDoc: (id, file, category, companionId, onProgress) => {
    const form = new FormData();
    form.append("file", file);
    form.append("category", category || "other");
    if (companionId) form.append("companion_id", companionId);
    return api.post(`/visa/requests/${id}/documents`, form, {
      headers: { "Content-Type": "multipart/form-data" },
      onUploadProgress: (e) => {
        if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
      },
    }).then((r) => r.data);
  },

  // Chunked resumable upload
  chunkedUpload: async (id, file, category, companionId, onProgress, opts = {}) => {
    const chunkSize = 1024 * 1024; // 1 MB
    const init = await api.post("/visa/uploads/init", {
      filename: file.name,
      mime_type: file.type,
      total_size: file.size,
      visa_request_id: id,
      category,
      companion_id: companionId,
    }).then((r) => r.data);
    const uploadId = init.upload_id;
    const totalChunks = Math.ceil(file.size / chunkSize);
    for (let i = 0; i < totalChunks; i++) {
      if (opts.pausedRef?.current) {
        // Pause: caller can resume by calling chunkedUpload again
        throw new Error("PAUSED");
      }
      const start = i * chunkSize;
      const end = Math.min(start + chunkSize, file.size);
      const blob = file.slice(start, end);
      const form = new FormData();
      form.append("chunk_index", String(i));
      form.append("chunk", blob, `${i}`);
      await api.post(`/visa/uploads/${uploadId}/chunk`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (onProgress) onProgress(Math.round(((i + 1) / totalChunks) * 100));
    }
    return await api.post(`/visa/uploads/${uploadId}/complete`).then((r) => r.data);
  },

  listDocs: (id) => api.get(`/visa/requests/${id}/documents`).then((r) => r.data),
  downloadDoc: (docId) => `${api.defaults.baseURL}/visa/documents/${docId}/download`,

  timeline: (id) => api.get(`/visa/requests/${id}/timeline`).then((r) => r.data),
  addNote: (id, title, description) =>
    api.post(`/visa/requests/${id}/timeline`, { title, description }).then((r) => r.data),

  previewLetterUrl: (id, templateId) =>
    `${api.defaults.baseURL}/visa/requests/${id}/letter/preview${templateId ? `?template_id=${templateId}` : ""}`,
  generateLetter: (id, templateId) =>
    api.post(`/visa/requests/${id}/letter/generate`, { template_id: templateId }).then((r) => r.data),
  letterVersions: (id) => api.get(`/visa/requests/${id}/letter/versions`).then((r) => r.data),
  letterDownloadUrl: (id, versionId) =>
    `${api.defaults.baseURL}/visa/requests/${id}/letter/${versionId}/download`,
  emailLetter: (id, versionId) =>
    api.post(`/visa/requests/${id}/letter/${versionId}/email`).then((r) => r.data),

  dashboard: () => api.get("/visa/dashboard").then((r) => r.data),
  travel: () => api.get("/visa/travel").then((r) => r.data),
  pickups: () => api.get("/visa/airport-pickups").then((r) => r.data),

  listTemplates: () => api.get("/visa/templates").then((r) => r.data),
  createTemplate: (body) => api.post("/visa/templates", body).then((r) => r.data),
  updateTemplate: (id, body) => api.patch(`/visa/templates/${id}`, body).then((r) => r.data),
  deleteTemplate: (id) => api.delete(`/visa/templates/${id}`).then((r) => r.data),
};
